import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../ui/Input';
import { CATEGORIES } from '../../constants/serviceCategories';
import { normalizeServiceBranchIds } from '../../utils/serviceBranches';
import { PAYMENT_SLIP_POLICY_OPTIONS } from '../../constants/paymentSlipPolicies';

const ServiceFormPanel = ({ open, onClose, service, branches, onSave, saving }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Hair');
  const [duration, setDuration] = useState(45);
  const [price, setPrice] = useState('');
  const [allBranches, setAllBranches] = useState(true);
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);
  const [requiresAdvance, setRequiresAdvance] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [paymentSlipPolicy, setPaymentSlipPolicy] = useState('OPTIONAL');
  const [isActive, setIsActive] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (service) {
      const ids = normalizeServiceBranchIds(service);
      setName(service.name);
      setDescription(service.description || '');
      setCategory(service.category);
      setDuration(service.duration);
      setPrice(String(service.price));
      setAllBranches(!ids?.length);
      setSelectedBranchIds(ids || []);
      setRequiresAdvance(!!service.requiresAdvance);
      setAdvanceAmount(service.requiresAdvance ? String(service.advanceAmount || '') : '');
      setPaymentSlipPolicy(service.paymentSlipPolicy || 'OPTIONAL');
      setIsActive(service.isActive);
      setImagePreview(service.imageUrl);
    } else {
      setName('');
      setDescription('');
      setCategory('Hair');
      setDuration(45);
      setPrice('');
      setAllBranches(true);
      setSelectedBranchIds([]);
      setRequiresAdvance(false);
      setAdvanceAmount('');
      setPaymentSlipPolicy('OPTIONAL');
      setIsActive(true);
      setImagePreview(null);
    }
    setImageFile(null);
  }, [open, service]);

  const toggleBranch = (branchId) => {
    setSelectedBranchIds((prev) =>
      prev.includes(branchId) ? prev.filter((id) => id !== branchId) : [...prev, branchId]
    );
  };

  if (!open) return null;

  const branchSelectionInvalid = !allBranches && selectedBranchIds.length === 0;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-lg flex-col border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 p-5">
          <h2 className="text-lg font-semibold">
            {service ? t('salon.services.editService') : t('salon.services.addService')}
          </h2>
          <button type="button" onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (branchSelectionInvalid) return;
            onSave({
              name,
              description,
              category,
              duration,
              price,
              branchIds: allBranches ? [] : selectedBranchIds,
              requiresAdvance,
              advanceAmount: requiresAdvance ? Number(advanceAmount || 0) : 0,
              paymentSlipPolicy,
              isActive,
              imageFile,
            });
          }}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="flex-1 space-y-4 overflow-y-auto p-5">
            <div
              className="rounded-xl border-2 border-dashed border-zinc-300 dark:border-zinc-700 p-6 text-center"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files[0];
                if (f) {
                  setImageFile(f);
                  setImagePreview(URL.createObjectURL(f));
                }
              }}
            >
              {imagePreview ? (
                <img src={imagePreview} alt="" className="mx-auto h-32 rounded-lg object-cover" />
              ) : (
                <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('salon.services.dragDropImage')}</p>
              )}
              <label className="mt-2 inline-block cursor-pointer text-sm text-glow-400">
                {t('common.browse')}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                  const f = e.target.files[0];
                  if (f) {
                    setImageFile(f);
                    setImagePreview(URL.createObjectURL(f));
                  }
                }} />
              </label>
            </div>
            <Input label={t('salon.services.nameRequired')} value={name} onChange={(e) => setName(e.target.value)} required />
            <div>
              <label className="mb-1 block text-sm text-zinc-600 dark:text-zinc-400">{t('common.category')}</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <p className="mb-2 text-sm text-zinc-600 dark:text-zinc-400">{t('salon.services.branchesOffered')}</p>
              <label className="mb-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={allBranches}
                  onChange={(e) => {
                    setAllBranches(e.target.checked);
                    if (e.target.checked) setSelectedBranchIds([]);
                  }}
                />
                {t('salon.services.allBranches')}
              </label>
              {!allBranches && (
                <div className="space-y-2 rounded-lg border border-zinc-200 dark:border-zinc-800 p-3">
                  {(branches || []).map((b) => (
                    <label key={b._id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedBranchIds.includes(b._id)}
                        onChange={() => toggleBranch(b._id)}
                      />
                      {b.name}
                    </label>
                  ))}
                  {branchSelectionInvalid && (
                    <p className="text-xs text-red-400">{t('salon.services.selectAtLeastOneBranch')}</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-4">
              <Input label={t('salon.services.duration')} type="number" min={1} value={duration} onChange={(e) => setDuration(e.target.value)} />
              <span className="self-end pb-2 text-sm text-zinc-600 dark:text-zinc-500">{t('salon.services.minutes')}</span>
            </div>
            <Input label={t('salon.services.price')} type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} required />
            <div>
              <label className="mb-1 block text-sm text-zinc-600 dark:text-zinc-400">{t('payments.slipPolicyLabel')}</label>
              <select
                value={paymentSlipPolicy}
                onChange={(e) => setPaymentSlipPolicy(e.target.value)}
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
              >
                {PAYMENT_SLIP_POLICY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={requiresAdvance} onChange={(e) => setRequiresAdvance(e.target.checked)} />
              {t('payments.requiresAdvance')}
            </label>
            {requiresAdvance && (
              <Input
                label={t('payments.advanceAmount')}
                type="number"
                min={0}
                value={advanceAmount}
                onChange={(e) => setAdvanceAmount(e.target.value)}
              />
            )}
            <div>
              <label className="mb-1 block text-sm text-zinc-600 dark:text-zinc-400">{t('common.description')}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
              />
            </div>
            {service && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                {t('common.active')}
              </label>
            )}
          </div>
          <div className="border-t border-zinc-200 dark:border-zinc-800 p-5">
            <button
              type="submit"
              disabled={saving || branchSelectionInvalid}
              className="w-full rounded-lg bg-glow-600 py-2.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? t('common.saving') : t('salon.services.saveService')}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
};

export default ServiceFormPanel;
